// src/components/common/ToolIcon.jsx
//
// Tools store their icon as a Lucide component name string (e.g. "FileText").
//
// These are imported by name on purpose. Pulling in the whole lucide-react
// namespace instead costs ~130 kB gzipped — more than a third of the bundle —
// to offer 1,500 icons nobody scrolls through. This curated set covers the kinds
// of tools that live in this portal.
//
// TO ADD AN ICON: find it at lucide.dev, add it to the import below and to the
// ICONS map. The name in the map is exactly what gets stored on the tool.

import React, { useState } from 'react';
import { iconUrl } from '../../services/toolIcons';
import { cx } from './ui';
import {
  // Words and documents
  FileText, FileSearch, PenTool, Type, Languages, BookOpen, ScrollText, Quote,
  NotebookPen, Mail, MessageSquare, MessagesSquare, Newspaper, Subtitles,
  // AI
  Bot, Sparkles, BrainCircuit, Wand2, Zap, Cpu,
  // Visual and brand
  Palette, Image, Images, Camera, Video, Film, Layers, Shapes, Frame,
  Presentation, LayoutTemplate, Crop, Brush, Contrast,
  // Research and measurement
  Radar, Search, TrendingUp, BarChart3, LineChart, PieChart, Gauge, Telescope,
  Microscope, Globe, Compass, Target, Lightbulb,
  // Data
  Database, Table2, FileSpreadsheet, Filter, Binary,
  // Operations
  CalendarCheck, CalendarDays, Clock, Receipt, Ticket, ClipboardList, ListChecks,
  Workflow, Settings2, Boxes, Package, Truck, Building2, Users, UserCheck,
  Briefcase, Wallet, CreditCard, ShieldCheck, KeyRound, Lock, Server, Cloud,
  Link2, Share2, Rocket, Wrench, Hammer, Plug,
} from 'lucide-react';

export const ICONS = {
  FileText, FileSearch, PenTool, Type, Languages, BookOpen, ScrollText, Quote,
  NotebookPen, Mail, MessageSquare, MessagesSquare, Newspaper, Subtitles,
  Bot, Sparkles, BrainCircuit, Wand2, Zap, Cpu,
  Palette, Image, Images, Camera, Video, Film, Layers, Shapes, Frame,
  Presentation, LayoutTemplate, Crop, Brush, Contrast,
  Radar, Search, TrendingUp, BarChart3, LineChart, PieChart, Gauge, Telescope,
  Microscope, Globe, Compass, Target, Lightbulb,
  Database, Table2, FileSpreadsheet, Filter, Binary,
  CalendarCheck, CalendarDays, Clock, Receipt, Ticket, ClipboardList, ListChecks,
  Workflow, Settings2, Boxes, Package, Truck, Building2, Users, UserCheck,
  Briefcase, Wallet, CreditCard, ShieldCheck, KeyRound, Lock, Server, Cloud,
  Link2, Share2, Rocket, Wrench, Hammer, Plug,
};

export const ICON_NAMES = Object.keys(ICONS);

const FALLBACK = 'Boxes';

export const isKnownIcon = (name) => Object.prototype.hasOwnProperty.call(ICONS, name);

// Pass `tool` and it resolves everything: an uploaded logo wins, otherwise the
// glyph the tool names, otherwise the fallback (a tool saved before an icon was
// retired still has to render something). `name` alone stays supported for the
// icon picker, which chooses among glyphs and knows nothing about tools.
const ToolIcon = ({ tool, name, fill = false, className = 'h-5 w-5', ...props }) => {
  const src = iconUrl(tool);

  // Which object-fit suits the upload can't be known until it loads, so start
  // with the one that never destroys anything. A near-square image — what an
  // app icon almost always is — then switches to cover so it fills the tile
  // edge to edge; anything wider or taller stays contained and uncropped,
  // because silently cutting the sides off someone's wordmark is worse than a
  // little empty space.
  const [fit, setFit] = useState('object-contain');

  const onLoad = (event) => {
    const { naturalWidth: w, naturalHeight: h } = event.currentTarget;
    if (w && h && Math.max(w / h, h / w) < 1.2) setFit('object-cover');
  };

  if (src) {
    return (
      <img
        src={src}
        alt=""
        onLoad={onLoad}
        // Inside a tile the image IS the tile; the glyph sizing doesn't apply.
        className={cx(fit, fill ? 'h-full w-full' : className)}
        loading="lazy"
        draggable={false}
        {...props}
      />
    );
  }

  const Icon = ICONS[name ?? tool?.icon] || ICONS[FALLBACK];
  return <Icon className={className} aria-hidden="true" {...props} />;
};

export default ToolIcon;
